import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { useSession } from '../lib/auth';
import UserProfile from './UserProfile';
import LoginModal from './LoginModal';
import { LogIn, Menu, X } from 'lucide-react';

function Navigation() {
  const { data: session } = useSession();
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Close mobile menu when clicking outside or pressing Escape
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      // Don't close if clicking on the hamburger button or menu container
      if (isMobileMenuOpen &&
          !target.closest('#mobile-menu-container') &&
          !target.closest('[aria-label="Toggle mobile menu"]')) {
        setIsMobileMenuOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      // Delay adding the click listener to avoid immediate closure
      const timer = setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 100);

      document.addEventListener('keydown', handleEscape);
      // Prevent body scrolling when menu is open
      document.body.style.overflow = 'hidden';

      return () => {
        clearTimeout(timer);
        document.removeEventListener('click', handleClickOutside);
        document.removeEventListener('keydown', handleEscape);
        document.body.style.overflow = '';
      };
    } else {
      document.body.style.overflow = '';
    }
  }, [isMobileMenuOpen]);




  return (
    <>
      <header className="bg-white shadow-md border-b border-gray-100 relative">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-2 md:py-3">
          <div className="flex items-center justify-between">
            {/* Logo - Mobile: Icon only, Desktop: Icon + Text */}
            <NavLink to="/" className="flex items-center space-x-2 md:space-x-5">
              <img
                src="/logos/frontend/logo.svg"
                alt="Snag Your Tee"
                className="w-8 h-8 md:w-14 md:h-14 object-contain"
              />
              <div className="hidden md:block">
                <h1 className="text-xl font-bold text-gray-900">
                  Snag Your Tee
                </h1>
              </div>
            </NavLink>

            <div className="flex items-center space-x-2 md:space-x-4">
              {/* Mobile: Core Navigation + Hamburger Menu */}
              <div className="flex items-center md:hidden">
                {/* Core Navigation - Always Visible on Mobile */}
                <nav className="flex space-x-2">
                  <NavLink
                    to="/tee-times"
                    className={({ isActive }) =>
                      `px-2 py-2 font-medium text-sm transition-colors duration-200 ${
                        isActive
                          ? 'text-green-600'
                          : 'text-gray-600 hover:text-green-600'
                      }`
                    }
                  >
                    Times
                  </NavLink>
                  <NavLink
                    to="/waitlist"
                    className={({ isActive }) =>
                      `px-2 py-2 font-medium text-sm transition-colors duration-200 ${
                        isActive
                          ? 'text-green-600'
                          : 'text-gray-600 hover:text-green-600'
                      }`
                    }
                  >
                    Waitlist
                  </NavLink>
                </nav>

                {/* Hamburger Menu Button */}
                <button
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="p-2 text-gray-600 hover:text-green-600 transition-colors duration-200"
                  aria-label="Toggle mobile menu"
                >
                  {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
                </button>
              </div>

              {/* Desktop: Full Navigation */}
              <div className="hidden md:flex items-center space-x-6">
                <nav className="flex space-x-6">
                  <NavLink
                    to="/tee-times"
                    className={({ isActive }) =>
                      `px-4 py-2 font-medium text-base transition-colors duration-200 ${
                        isActive
                          ? 'text-green-600'
                          : 'text-gray-600 hover:text-green-600'
                      }`
                    }
                  >
                    Tee Times
                  </NavLink>
                  <NavLink
                    to="/waitlist"
                    className={({ isActive }) =>
                      `px-4 py-2 font-medium text-base transition-colors duration-200 ${
                        isActive
                          ? 'text-green-600'
                          : 'text-gray-600 hover:text-green-600'
                      }`
                    }
                  >
                    Waitlist
                  </NavLink>
                  <NavLink
                    to="/courses"
                    className={({ isActive }) =>
                      `px-4 py-2 font-medium text-base transition-colors duration-200 ${
                        isActive
                          ? 'text-green-600'
                          : 'text-gray-600 hover:text-green-600'
                      }`
                    }
                  >
                    Courses
                  </NavLink>
                </nav>

                {/* User Authentication */}
                {session?.user ? (
                  <UserProfile />
                ) : (
                  <button
                    onClick={() => setIsLoginModalOpen(true)}
                    className="flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                  >
                    <LogIn size={16} />
                    <span>Sign In</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Dropdown Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-white shadow-lg border-t border-gray-100 z-50 transform transition-all duration-300 ease-out">
            <div id="mobile-menu-container" className="px-6 py-6 space-y-2">
              <NavLink
                to="/courses"
                onClick={() => setIsMobileMenuOpen(false)}
                className={({ isActive }) =>
                  `block px-4 py-4 text-sm font-medium rounded-lg transition-all duration-200 ${
                    isActive
                      ? 'text-green-600 bg-green-50'
                      : 'text-gray-800 hover:text-green-600 hover:bg-green-50'
                  }`
                }
              >
                Courses
              </NavLink>

              {/* 分割线 */}
              <div className="border-t border-gray-200 my-2"></div>

              {session?.user ? (
                <div className="px-4 py-2 text-gray-600 text-sm">
                  <UserProfile />
                </div>
              ) : (
                <button
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    setIsLoginModalOpen(true);
                  }}
                  className="block w-full px-4 py-4 text-sm font-medium text-gray-800 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200 text-left"
                >
                  Sign In
                </button>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Login Modal */}
      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        title="Welcome to Snag Your Tee"
        message="Sign in to manage your golf tee time waitlists and get personalized notifications."
      />
    </>
  );
}

export default Navigation;